import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { StarRating } from '../../components/StarRating';
import './EventFeedback.css';

interface FeedbackStatus {
  hasSubmitted: boolean;
  userMealType: string | null;
  event: {
    name: string;
    date: string;
    location: string;
    organizerName: string;
  };
}

interface FeedbackData {
  overallRating: number;
  platformRating: number;
  organizerRating: number;
  testimonial?: {
    text: string;
    allowPublicUse: boolean;
    participantName: string;
  };
  mealExperience?: {
    mealType: string;
    mealName: string;
    description: string;
    estimatedCost: number;
  };
}

export const EventFeedback: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [status, setStatus] = useState<FeedbackStatus | null>(null);

  const [overallRating, setOverallRating] = useState(0);
  const [platformRating, setPlatformRating] = useState(0);
  const [organizerRating, setOrganizerRating] = useState(0);
  const [testimonial, setTestimonial] = useState('');
  const [allowPublicUse, setAllowPublicUse] = useState(true);
  const [testimonialName, setTestimonialName] = useState(user?.name || '');
  const [includeMealFeedback, setIncludeMealFeedback] = useState(false);
  const [mealName, setMealName] = useState('');
  const [mealDescription, setMealDescription] = useState('');
  const [estimatedCost, setEstimatedCost] = useState('');

  useEffect(() => {
    fetchFeedbackStatus();
  }, [eventId]);

  const fetchFeedbackStatus = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/feedback/${eventId}/status`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        setError(data.message || 'Kunne ikke hente tilbakemelding status');
        setLoading(false);
        return;
      }

      const data = await response.json();
      setStatus(data);

      if (data.hasSubmitted) {
        setError('Du har allerede sendt inn tilbakemelding for dette arrangementet.');
      }

      setLoading(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'En feil oppstod'
      );
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (overallRating === 0 || platformRating === 0 || organizerRating === 0) {
      setError('Vennligst gi en vurdering for alle kategoriene');
      return;
    }

    if (testimonial.trim() && allowPublicUse && !testimonialName.trim()) {
      setError('Vennligst oppgi navnet ditt hvis du tillater offentlig bruk av tilbakemeldingen');
      return;
    }

    if (includeMealFeedback) {
      if (!mealName.trim() || !mealDescription.trim() || !estimatedCost) {
        setError('Vennligst fyll ut alle feltene for måltidstilbakemelding');
        return;
      }
      const cost = parseFloat(estimatedCost);
      if (isNaN(cost) || cost < 0) {
        setError('Vennligst oppgi en gyldig kostnad');
        return;
      }
    }

    const feedbackData: FeedbackData = {
      overallRating,
      platformRating,
      organizerRating,
    };

    if (testimonial.trim()) {
      feedbackData.testimonial = {
        text: testimonial.trim(),
        allowPublicUse: allowPublicUse,
        participantName: testimonialName.trim()
      };
    }

    if (includeMealFeedback && status?.userMealType) {
      // Convert Norwegian meal type to English
      const mealTypeMap: { [key: string]: string } = {
        'Forrett': 'appetizer',
        'Hovedrett': 'mainCourse',
        'Dessert': 'dessert'
      };
      
      feedbackData.mealExperience = {
        mealType: mealTypeMap[status.userMealType] || 'mainCourse',
        mealName: mealName.trim(),
        description: mealDescription.trim(),
        estimatedCost: parseFloat(estimatedCost),
      };
    }

    setSubmitting(true);

    try {
      console.log('Submitting feedback data:', JSON.stringify(feedbackData, null, 2));
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/feedback/${eventId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(feedbackData),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        console.error('Feedback submission error:', data);
        throw new Error(data.message || data.error || 'Kunne ikke sende tilbakemelding');
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/deltaker/mine-fester');
      }, 3000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'En feil oppstod ved innsending'
      );
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="feedback-container">
        <div className="loading">Laster...</div>
      </div>
    );
  }

  if (error && !status) {
    return (
      <div className="feedback-container">
        <div className="error-message">{error}</div>
        <button onClick={() => navigate('/deltaker/mine-fester')} className="back-button">
          Tilbake til mine arrangementer
        </button>
      </div>
    );
  }

  if (status?.hasSubmitted) {
    return (
      <div className="feedback-container">
        <div className="success-message">
          Du har allerede sendt inn tilbakemelding for dette arrangementet. Takk!
        </div>
        <button onClick={() => navigate('/deltaker/mine-fester')} className="back-button">
          Tilbake til mine arrangementer
        </button>
      </div>
    );
  }

  if (success) {
    return (
      <div className="feedback-container">
        <div className="success-message">
          <h2>Takk for tilbakemeldingen!</h2>
          <p>Din tilbakemelding er mottatt og vil hjelpe oss å forbedre Sykkelfest.</p>
          <p>Du blir sendt tilbake til dine arrangementer om litt...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="feedback-container">
      <div className="feedback-card">
        <h1>Tilbakemelding på Sykkelfest</h1>
        
        {status && (
          <div className="event-info">
            <h2>{status.event.name}</h2>
            <p>
              {new Date(status.event.date).toLocaleDateString('nb-NO', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </p>
            <p>{status.event.location}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="feedback-form">
          {error && <div className="error-message">{error}</div>}

          <div className="ratings-section">
            <h3>Gi din vurdering</h3>
            
            <StarRating
              label="Sykkelfest opplevelse generelt"
              rating={overallRating}
              onRatingChange={setOverallRating}
            />

            <StarRating
              label="Plattform (nettside, påmelding, kommunikasjon)"
              rating={platformRating}
              onRatingChange={setPlatformRating}
            />

            <StarRating
              label={`Arrangør: ${status?.event.organizerName || 'Arrangøren'}`}
              rating={organizerRating}
              onRatingChange={setOrganizerRating}
            />
          </div>

          <div className="testimonial-section">
            <h3>Tilbakemelding (valgfritt)</h3>
            <textarea
              value={testimonial}
              onChange={(e) => setTestimonial(e.target.value)}
              placeholder="Del din opplevelse med Sykkelfest..."
              rows={5}
              className="testimonial-textarea"
            />

            {testimonial.trim() && (
              <div className="public-use-section">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={allowPublicUse}
                    onChange={(e) => setAllowPublicUse(e.target.checked)}
                  />
                  <span>Jeg tillater at denne tilbakemeldingen publiseres på nettsiden</span>
                </label>

                {allowPublicUse && (
                  <div className="name-input-section">
                    <label htmlFor="testimonial-name">Ditt navn (vises sammen med tilbakemeldingen)</label>
                    <input
                      id="testimonial-name"
                      type="text"
                      value={testimonialName}
                      onChange={(e) => setTestimonialName(e.target.value)}
                      placeholder="Ola Nordmann"
                      className="name-input"
                      required={allowPublicUse}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {status?.userMealType && (
            <div className="meal-feedback-section">
              <h3>Måltidstilbakemelding (valgfritt)</h3>
              <p className="meal-type-info">
                Du hadde ansvar for: <strong>{status.userMealType}</strong>
              </p>

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={includeMealFeedback}
                  onChange={(e) => setIncludeMealFeedback(e.target.checked)}
                />
                <span>Jeg vil dele min måltidsopplevelse for å hjelpe andre deltakere</span>
              </label>

              {includeMealFeedback && (
                <div className="meal-inputs">
                  <div className="form-group">
                    <label htmlFor="meal-name">Navn på retten</label>
                    <input
                      id="meal-name"
                      type="text"
                      value={mealName}
                      onChange={(e) => setMealName(e.target.value)}
                      placeholder="F.eks. Bruschetta med tomat og basilikum"
                      className="meal-input"
                      required={includeMealFeedback}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="meal-description">Beskrivelse</label>
                    <textarea
                      id="meal-description"
                      value={mealDescription}
                      onChange={(e) => setMealDescription(e.target.value)}
                      placeholder="Beskriv retten, ingredienser, tilberedning, vanskelighetsgrad, og eventuelle tips..."
                      rows={4}
                      className="meal-textarea"
                      required={includeMealFeedback}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="estimated-cost">Estimert kostnad totalt (NOK)</label>
                    <input
                      id="estimated-cost"
                      type="number"
                      value={estimatedCost}
                      onChange={(e) => setEstimatedCost(e.target.value)}
                      placeholder="F.eks. 250"
                      min="0"
                      step="1"
                      className="cost-input"
                      required={includeMealFeedback}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/deltaker/mine-fester')}
              className="cancel-button"
              disabled={submitting}
            >
              Avbryt
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={submitting}
            >
              {submitting ? 'Sender...' : 'Send tilbakemelding'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
